import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import {
  getSavedCollections,
  getSavedRoutes,
  getUserRoutes,
} from '../services/api';

/**
 * Сохранённое, кроме мест: подборки, маршруты редакции и личные маршруты.
 * Места живут отдельно, в FavoritesContext.
 *
 * Зачем контекст. Счётчики в профиле обязаны меняться сразу после
 * сохранения или удаления — как у мест. Пока каждый экран грузил эти
 * списки себе, число в профиле оставалось тем, что было на момент его
 * загрузки: страница лежит в LRU-кеше и повторно не монтируется.
 *
 * Списки правятся локально, без перезапроса: сервер уже подтвердил
 * операцию, второй круг по сети ничего не уточнит, но задержит цифру.
 */
const CollectionsContext = createContext();

export const useCollections = () => {
  const context = useContext(CollectionsContext);
  if (!context) {
    throw new Error('useCollections must be used within CollectionsProvider');
  }
  return context;
};

export const CollectionsProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [savedCollections, setSavedCollections] = useState([]);
  const [savedRoutes, setSavedRoutes] = useState([]);
  const [userRoutes, setUserRoutes] = useState([]);
  const [loading, setLoading] = useState(false);

  /**
   * Загрузка всех трёх списков.
   *
   * `background: true` — тихое обновление: то, что уже на экране,
   * не подменяется лоадером. То же соглашение, что в FavoritesContext.
   */
  const loadSaved = useCallback(async ({ background = false } = {}) => {
    if (!isAuthenticated) {
      setSavedCollections([]);
      setSavedRoutes([]);
      setUserRoutes([]);
      return;
    }

    if (!background) setLoading(true);

    // Списки независимы: упавший запрос не должен обнулять два других
    const [collections, routes, personal] = await Promise.allSettled([
      getSavedCollections(),
      getSavedRoutes(),
      getUserRoutes(),
    ]);

    // Оборванный запрос не стирает то, что пользователь уже видит
    if (collections.status === 'fulfilled') setSavedCollections(collections.value.data || []);
    if (routes.status === 'fulfilled') setSavedRoutes(routes.value.data || []);
    if (personal.status === 'fulfilled') setUserRoutes(personal.value.data || []);

    if (!background) setLoading(false);
  }, [isAuthenticated]);

  // Правки после подтверждённой сервером операции: вызывающий код уже
  // сходил в API, здесь только синхронизация состояния
  const addSavedCollection = useCallback((savedCollection) => {
    if (!savedCollection) return;
    setSavedCollections(prev =>
      prev.some(c => c.id === savedCollection.id) ? prev : [savedCollection, ...prev]
    );
  }, []);

  const removeSavedCollection = useCallback((savedCollectionId) => {
    setSavedCollections(prev => prev.filter(c => c.id !== savedCollectionId));
  }, []);

  const addSavedRoute = useCallback((savedRoute) => {
    if (!savedRoute) return;
    setSavedRoutes(prev =>
      prev.some(r => r.id === savedRoute.id) ? prev : [savedRoute, ...prev]
    );
  }, []);

  const removeSavedRoute = useCallback((savedRouteId) => {
    setSavedRoutes(prev => prev.filter(r => r.id !== savedRouteId));
  }, []);

  const addUserRoute = useCallback((userRoute) => {
    if (!userRoute) return;
    setUserRoutes(prev =>
      prev.some(r => r.id === userRoute.id) ? prev : [userRoute, ...prev]
    );
  }, []);

  const removeUserRoute = useCallback((userRouteId) => {
    setUserRoutes(prev => prev.filter(r => r.id !== userRouteId));
  }, []);

  // Правка своего маршрута на прогулке: название и места в списке
  // «Избранного» меняются сразу, без перезапроса
  const updateUserRoute = useCallback((userRoute) => {
    if (!userRoute) return;
    setUserRoutes(prev => prev.map(r => (r.id === userRoute.id ? { ...r, ...userRoute } : r)));
  }, []);

  useEffect(() => {
    loadSaved();
  }, [loadSaved]);

  // Стабильная ссылка: иначе объект пересоздаётся на каждый рендер
  // и перерисовывает всех потребителей
  const value = useMemo(() => ({
    savedCollections,
    savedRoutes,
    userRoutes,
    // Во вкладке «Маршруты» лежат и личные, и сохранённые — счётчик общий
    routesCount: savedRoutes.length + userRoutes.length,
    collectionsCount: savedCollections.length,
    loading,
    loadSaved,
    // Прежнее имя: им пользуется CollectionPage
    loadSavedCollections: loadSaved,
    addSavedCollection,
    removeSavedCollection,
    addSavedRoute,
    removeSavedRoute,
    addUserRoute,
    removeUserRoute,
    updateUserRoute,
  }), [
    savedCollections,
    savedRoutes,
    userRoutes,
    loading,
    loadSaved,
    addSavedCollection,
    removeSavedCollection,
    addSavedRoute,
    removeSavedRoute,
    addUserRoute,
    removeUserRoute,
    updateUserRoute,
  ]);

  return (
    <CollectionsContext.Provider value={value}>
      {children}
    </CollectionsContext.Provider>
  );
};

export default CollectionsContext;
